import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  getDailyAttendance,
  getCurrentlyInOffice,
  getRecentMovements,
} from "../../services/apiService";
import LoadingButton from "../LoadingButton/LoadingButton";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import "./AttendanceContent.css";

const AttendanceContent = () => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("daily");

  // Tarihi memoize et
  const currentDate = useMemo(() => new Date().toDateString(), []);

  // Format fonksiyonlarını memoize et
  const formatWorkHours = useCallback((hours) => {
    if (hours === null || hours === undefined || hours === "") return "-";
    const num = parseFloat(hours);
    if (isNaN(num)) return "-";
    const sign = num < 0 ? "-" : "";
    const absHours = Math.abs(num);
    const h = Math.floor(absHours);
    const m = Math.round((absHours - h) * 60);
    if (m === 60) {
      return `${sign}${h + 1}:00`;
    }
    return `${sign}${h}:${m.toString().padStart(2, "0")}`;
  }, []);

  const formatTime = useCallback((timeString) => {
    if (!timeString) return "-";
    try {
      return new Date(timeString).toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "-";
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("tr-TR");
    } catch (error) {
      return "-";
    }
  }, []);

  const getPersonName = useCallback((record) => {
    if (record.name) {
      return `${record.name} ${record.surname || ""}`.trim();
    }
    return "Bilinmiyor";
  }, []);

  // Veri çekme fonksiyonunu memoize et
  const fetchAttendance = useCallback(async () => {
    if (!refreshing) setLoading(true);
    setError("");
    try {
      let data = [];
      if (activeTab === "daily") {
        data = await getDailyAttendance();
      } else if (activeTab === "current") {
        data = await getCurrentlyInOffice();
      } else if (activeTab === "recent") {
        data = await getRecentMovements();
      }
      console.log(`${activeTab} verisi çekildi:`, data);
      const finalData = Array.isArray(data) ? data : [];
      setAttendance(finalData);
      if (finalData.length === 0) {
        console.warn(`⚠️ ${activeTab} için veri bulunamadı`);
      }
    } catch (error) {
      console.error("❌ Devam durumu çekilemedi:", error);
      setError(`Veri yüklenirken hata oluştu: ${error.message}`);
      setAttendance([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab, refreshing]);

  // Veri çekme için useEffect
  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Gün değişikliğini kontrol eden useEffect
  useEffect(() => {
    const dayCheckInterval = setInterval(() => {
      const today = new Date().toDateString();
      if (today !== currentDate) {
        fetchAttendance();
      }
    }, 60000);

    return () => clearInterval(dayCheckInterval);
  }, [currentDate, fetchAttendance]);

  // Yenileme butonu için memoize edilmiş fonksiyon
  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    fetchAttendance();
  }, [fetchAttendance]);

  // Tablo sütunlarını dinamik oluştur
  const tableColumns = useMemo(() => {
    if (activeTab === "daily") {
      return ["Personel", "Giriş", "Çıkış", "Çalışma Süresi", "Durum"];
    } else if (activeTab === "current") {
      return ["Personel", "RFID", "Durum"];
    } else if (activeTab === "recent") {
      return ["Personel", "Tarih", "Saat", "İşlem", "Not"];
    }
    return [];
  }, [activeTab]);

  if (loading) {
    return (
      <div className="attendance-content">
        <div className="attendance-list">
          <div className="list-header">
            <h2>Devam Kontrolü</h2>
          </div>
          <div className="loading-container">
            <div className="orbit-loader-wrapper">
              <OrbitLoaderComponent size="medium" showPath={true} />
            </div>
            <p className="loading-text">Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance-content">
      <div className="attendance-list">
        <div className="list-header">
          <div className="header-content">
            <h2>Devam Kontrolü</h2>
          </div>
          <div className="tab-buttons">
            <LoadingButton
              className={`tab-btn ${activeTab === "daily" ? "active" : ""}`}
              onClick={() => setActiveTab("daily")}
              isLoading={false}
            >
              Günlük Durum
            </LoadingButton>
            <LoadingButton
              className={`tab-btn ${activeTab === "current" ? "active" : ""}`}
              onClick={() => setActiveTab("current")}
              isLoading={false}
            >
              Ofiste Bulunanlar
            </LoadingButton>
            <LoadingButton
              className={`tab-btn ${activeTab === "recent" ? "active" : ""}`}
              onClick={() => setActiveTab("recent")}
              isLoading={false}
            >
              Son Hareketler
            </LoadingButton>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <p>❌ {error}</p>
            <LoadingButton
              isLoading={false}
              onClick={fetchAttendance}
              className="retry-button"
            >
              Tekrar Dene
            </LoadingButton>
          </div>
        )}

        <div
          className="table-container"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <table className="attendance-table">
            <thead>
              <tr>
                {tableColumns.map((column, index) => (
                  <th key={index}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {attendance.length > 0 ? (
                attendance.map((record, index) => (
                  <tr
                    key={
                      record.log_id ||
                      record.attendance_id ||
                      record.user_id ||
                      index
                    }
                  >
                    <td>
                      <strong>{getPersonName(record)}</strong>
                    </td>
                    {activeTab === "daily" && (
                      <>
                        <td>{formatTime(record.first_in_time)}</td>
                        <td>
                          {formatTime(record.last_out_time) || "Çıkış Yapmadı"}
                        </td>
                        <td>
                          <span className="work-hours">
                            {formatWorkHours(record.total_hours)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`status-badge daily-status ${
                              record.status ? "present" : "absent"
                            }`}
                          >
                            {record.status
                              ? "✅ Giriş Yaptı"
                              : "❌ Giriş Yapmadı"}
                          </span>
                        </td>
                      </>
                    )}
                    {activeTab === "current" && (
                      <>
                        <td>
                          <code>{record.rfid_tag || "N/A"}</code>
                        </td>
                        <td>
                          <span className="status-badge present">OFİSTE</span>
                        </td>
                      </>
                    )}
                    {activeTab === "recent" && (
                      <>
                        <td>{formatDate(record.access_time)}</td>
                        <td>{formatTime(record.access_time)}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              record.access_type === "IN" ? "present" : "absent"
                            }`}
                          >
                            {record.access_type === "IN" ? "GİRİŞ" : "ÇIKIŞ"}
                          </span>
                        </td>
                        <td>{record.note || "-"}</td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableColumns.length} className="no-data">
                    📭 Veri bulunamadı
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AttendanceContent);
